/** Copyright (c) 2019 T.Rowe Price. All rights reserved. */

import {
  groupBy,
  sortBy,
  map,
  orderBy,
  isNil,
  isEmpty,
  pick,
  trim,
  uniq,
  find
} from 'lodash';
import { createSelector } from 'reselect';
import moment from 'moment';
import { getActiveCustomAccountGroup } from 'state/preferences/PreferencesReducer';
import { getBalancesCurrentDate } from 'state/account/AccountOverviewTableSelectors';
import { getRouter } from 'state/router/RouterSelectors';
import {
  getCustomerBusinessPartyId,
  getOrderedCustomerTrustedContacts
} from 'state/customer/CustomerSelectors';
import { isDashboardEntryPoint } from 'state/session/SessionSelectors';
import { formatAccountNumber } from 'services/Formats';
import {
  DISTRIBUTE_TO_BANK_ID,
  DISTRIBUTE_TO_FUND_ID,
  DIVIDEND_TYPE_ID,
  getBroadAssetClassName
} from 'services/Preferences';
import {
  getAccountsById,
  getAccountsWithIntraday,
  getIntradayAccounts,
  getNonPurgedAccounts,
  getUserMutualFunds
} from 'state/account/AccountSelectors';
import { getView } from 'state/view/ViewSelectors';
import {
  isActiveAdvisory,
  isBrokerageOrActivePlus,
  isMutualFund,
  productTypeNameFromNumber,
  isCollegeSavings,
  isActivePlus
} from 'services/Products';
import { isIRA } from 'services/AccountTypes';
import {
  ownershipMapping,
  VIEW_ONLY,
  accountGrouping,
  ownerGrouping,
  groupingFunctionalMapping,
  mapAccountsToTableData,
  isProductSelection
} from 'services/TableSupport';
import {
  extractAndFormatOwners,
  getAccountDescriptor,
  isOwnerOnAgreementByBPID,
} from 'services/Accounts';
import { areBanksWithoutIdEqual } from 'services/LinkedBankAccounts';
import {
  isRetirementFund,
  isRetirementIncome
} from 'components/shared/DivCapGains/RetirementUtils';

export const getDefaultGroupBy = (state) =>
  state.customerPreferences.defaultGroupBy;

export const getSelectedDefaultViewSettings = (state) =>
  state.selectedDefaultViewSettings;

export const getPreferredAuthenticationMethod = (state) =>
  state.customerPreferences.preferredAuthenticationMethod;

export const getViewAccessRelationships = (state) => {
  if (
    !state.customerPreferences ||
    !state.customerPreferences.customerViewAccesses ||
    !state.customerPreferences.customerViewAccesses
  ) {
    return [];
  }
  return state.customerPreferences.customerViewAccesses;
};

const getDividendsCapitalGains = (state) => {
  return state.dividendsCapitalGains;
};

const comparator = (state, props) => props;

export const isAuthenticationMethod = createSelector(
  [getPreferredAuthenticationMethod, comparator],
  (authMethod, authComparator) => {
    if (authMethod && authComparator) {
      return authComparator.toUpperCase() === authMethod.toUpperCase();
    }
    return false;
  }
);

export const hasGrantedViewAccess = createSelector(
  getViewAccessRelationships,
  (viewAccessRelationships) => !isEmpty(viewAccessRelationships)
);

export const redirectToNewDefaultHomepage = createSelector(
  getRouter,
  getDefaultGroupBy,
  isDashboardEntryPoint,
  (router, defaultGroupBy, isDashboardEntry) => {
    const { preferredStartPage, hasRedirectedToDefault } = defaultGroupBy;
    const {
      location: { pathname }
    } = router;
    const redirectLocation = pathname == '/';
    if (
      isDashboardEntry &&
      redirectLocation &&
      !hasRedirectedToDefault &&
      defaultGroupBy.preferredStartPage
    ) {
      return trim(preferredStartPage) === 'balanceDetails';
    }
    return false;
  }
);

export const getTrustedContactsTableData = createSelector(
  getAccountsWithIntraday,
  (accounts) =>
    accounts.filter((account) => !account.purgedAccount && !account.isViewOnly)
);

export const getOrderedTrustedContactsTableData = createSelector(
  getTrustedContactsTableData,
  (accounts) => orderBy(accounts, groupingFunctionalMapping('default', true))
);

const createTrustedContactsAccountDetails = ({
  account,
  currentDate,
  isBrokerageOrAPP,
  sweepAccountMap,
  trustedContacts,
  customerBusinessPartyId
}) => {
  const defaultDetails = {
    isViewOnly: ownershipMapping(account) === VIEW_ONLY,
    isEditable: isOwnerOnAgreementByBPID(account, customerBusinessPartyId) ? true : false,
    name: {
      ...pick(
        account,
        'accountId',
        'accountName',
        'accountType',
        'cusipId',
        'owner',
        'retirementAccount',
        'sweepAccount'
      ),
      accountWChecksum: formatAccountNumber(account),
      owners: extractAndFormatOwners(account),
      productType: account.productType,
      ticker: !isActivePlus(account.productType) && account.ticker,
      ownershipType: ownershipMapping(account)
    },
    productType: account.productType
  };

  const sweepAccountWChecksum = sweepAccountMap[account.accountId];
  if (sweepAccountWChecksum) {
    defaultDetails.name.sweepAccountWChecksum = sweepAccountWChecksum;
  }

  if (isBrokerageOrAPP) {
    const { balanceAsOfDate } = account;
    const hasSameDate = moment.utc(balanceAsOfDate).isSame(currentDate);
    defaultDetails.name.asOfDate = hasSameDate ? null : balanceAsOfDate;
  }

  return {
    ...defaultDetails,
    trustedContacts,
    groupingClassName: defaultDetails.name.retirementAccount
      ? 'is-retirement'
      : 'non-retirement'
  };
};

export const getTrustedContactsByAccountId = createSelector(
  getOrderedCustomerTrustedContacts,
  (customerTrustedContacts) => groupBy(customerTrustedContacts, 'accountId')
);

export const getTrustedContactMappedGroups = createSelector(
  getBalancesCurrentDate,
  getTrustedContactsByAccountId,
  getOrderedTrustedContactsTableData,
  getCustomerBusinessPartyId,
  (
    currentDate,
    trustedContactsGroups,
    orderedAccounts,
    customerBusinessPartyId
  ) => {
    const accounts = orderedAccounts
      .filter(
        ({ productType }) =>
          isMutualFund(productType) ||
          isBrokerageOrActivePlus(productType) ||
          isActiveAdvisory(productType)
      )
      .filter((account) => ownershipMapping(account) !== VIEW_ONLY);
    return accounts.map((account) => {
      const sweepAccountMap = {};
      const { accountId, sweepAccountId } = account;
      if (sweepAccountId) {
        const accountWChecksum = formatAccountNumber(account);
        sweepAccountMap[sweepAccountId] = accountWChecksum;
      }

      const trustedContacts = trustedContactsGroups[accountId]
        ? trustedContactsGroups[accountId].slice(0, 2)
        : null;

      const isBrokerageOrAPP = isBrokerageOrActivePlus(account.productType);
      return createTrustedContactsAccountDetails({
        account,
        currentDate,
        isBrokerageOrAPP,
        sweepAccountMap,
        trustedContacts,
        customerBusinessPartyId
      });
    });
  }
);

export const getNonNullTrustedContactMappedGroups = createSelector(
  getTrustedContactMappedGroups,
  (trustedContactMappedGroups) =>
    trustedContactMappedGroups.filter(
      (account) => account.trustedContacts !== null
    )
);

export const generateTableGroupRows = (data) => {
  const accountTypeMapping = {};
  Object.values(data).forEach((curr) => {
    const { accountInfo } = curr;
    const { accountType, owners } = accountInfo;
    const key = `${accountType} ${owners}`;
    const accountTypeList = accountTypeMapping[key] || [];
    accountTypeMapping[key] = [...accountTypeList, curr];
  });

  const mappedTableData = Object.entries(accountTypeMapping).map(
    ([accountType, accounts]) => ({
      accountType: accounts[0].accountInfo.accountType
        ? accounts[0].accountInfo.accountType
        : accountType,
      accounts: sortBy(accounts, [(a) => a.accountInfo.accountName]),
      owners: accounts[0].accountInfo.owners,
      isRetirement: accounts[0].accountInfo.retirementAccount === true
    })
  );

  return mappedTableData.sort((a, b) => {
    if (a.accounts[0] && b.accounts[0]) {
      if (
        !a.accounts[0].accountInfo.retirementAccount ===
        !b.accounts[0].accountInfo.retirementAccount
      ) {
        return a.accountType.localeCompare(b.accountType);
      }
      return !a.accounts[0].accountInfo.retirementAccount ? -1 : 1;
    }
    return 0;
  });
};

export const getDividendsAndCapitalGainsTableDataV2 = createSelector(
  getDividendsCapitalGains,
  getAccountsById,
  getIntradayAccounts,
  (dividendsCapitalGains, accounts, infradayAccounts) => {
    const { bankAccounts, distributionOptions, withholdingOptions } =
      dividendsCapitalGains;
    const data = {};
    const newAccountsIds = infradayAccounts?.reduce(
      (acc, account) => ({ ...acc, [account.accountId]: account }),
      {}
    );
    const accountsById = { ...newAccountsIds, ...accounts };

    distributionOptions.forEach((dividendCapitalGain) => {
      const {
        brokeragePositionAssetType,
        distributionTypeId,
        pendingUpdateId,
        sourceAccountDescriptor,
        targetAccountDescriptor,
        typeId
      } = dividendCapitalGain;

      const sourceAccountId = sourceAccountDescriptor.accountId;
      const account = accountsById[sourceAccountId];
      const targetAccountId = targetAccountDescriptor
        ? targetAccountDescriptor.accountId
        : null;
      if (
        accountsById[sourceAccountId]?.isViewOnly ||
        accountsById[targetAccountId]?.isViewOnly
      ) {
        return;
      }

      // Non-Retirement Accounts
      if (account && !isRetirementFund(account)) {
        const {
          accountName,
          accountId,
          accountType,
          accountTypeId,
          retirementAccount,
          ticker,
          trpCode,
          closedAccount
        } = account;

        const accountWChecksum = formatAccountNumber(account);
        const owners = extractAndFormatOwners(account);
        const isIncome2020 = isRetirementIncome(trpCode);
        const brokerageAssetType = brokeragePositionAssetType;

        if (isNil(data[sourceAccountId])) {
          data[accountId] = {};
          data[accountId].accountInfo = {
            ...account,
            accountName,
            accountType,
            accountTypeId,
            accountId,
            accountWChecksum,
            brokerageAssetType,
            distributionTypeId,
            isIncome2020,
            owners,
            retirementAccount,
            sourceAccountDescriptor,
            ticker,
            closedAccount
          };
        }

        const columnInfo = {
          distributionTypeId,
          pendingUpdateId
        };

        if (distributionTypeId === DISTRIBUTE_TO_FUND_ID) {
          const fundAccount = accountsById[targetAccountId] || {};
          if (fundAccount) {
            const {
              accountName,
              accountId,
              accountType,
              accountTypeId,
              ticker,
              closedAccount
            } = fundAccount;

            const accountWChecksum = formatAccountNumber(fundAccount);
            const owners = extractAndFormatOwners(fundAccount);
            const fundInfo = {
              accountName,
              accountId,
              accountType,
              accountTypeId,
              accountWChecksum,
              owners,
              ticker,
              closedAccount,
              //Adding this as a safeguard - account descriptor was only populated in the payload if the fund changed
              accountDescriptor: getAccountDescriptor(fundAccount),
              sourceAccountDescriptor
            };
            columnInfo.fundInfo = fundInfo;
          }
        }
        if (distributionTypeId === DISTRIBUTE_TO_BANK_ID) {
          if (dividendCapitalGain && dividendCapitalGain.bankAgreementKey) {
            const {
              bankAgreementKey: { bankId }
            } = dividendCapitalGain;
            const bankAccount = bankAccounts.find(
              (bankAccount) =>
                bankId &&
                bankAccount.bankAgreementKey &&
                bankAccount.bankAgreementKey.bankId === bankId
            );
            const dupeBankDetails = bankAccounts.find((bankAccount) =>
              areBanksWithoutIdEqual(
                bankAccount.bankAgreementKey,
                dividendCapitalGain.bankAgreementKey
              )
            );

            // seems unnecessary but because of bad bank ids we need pull bank details from duplicate match and retain submitted bank id
            if (!bankAccount && bankId && dupeBankDetails) {
              const bankDetails = {
                ...dupeBankDetails
              };
              bankDetails.bankId = bankId;
              columnInfo.bankInfo = bankDetails;
            } else {
              columnInfo.bankInfo = bankAccount;
            }
          }
        }

        if (typeId === DIVIDEND_TYPE_ID) {
          data[accountId].dividends = columnInfo;
        } else {
          data[accountId].capitalGains = columnInfo;
        }
      }

      // Retirement Accounts
      if (account && isRetirementFund(account)) {
        const withholdingOption = withholdingOptions.find(
          (withholdingOption) =>
            withholdingOption.accountDescriptor.accountId === sourceAccountId
        );
        let withholdingRate;
        let isWithholding = false;
        if (withholdingOption) {
          withholdingRate =
            withholdingOption.withholdingRate === 0
              ? 10
              : withholdingOption.withholdingRate;
          isWithholding = !withholdingOption.withholdingExempt;
        }
        const {
          accountName,
          accountType,
          accountId,
          accountTypeId,
          retirementAccount,
          ticker,
          closedAccount
        } = account;
        const accountWChecksum = formatAccountNumber(account);
        const owners = extractAndFormatOwners(account);
        const brokerageAssetType = brokeragePositionAssetType;
        if (isNil(data[accountId])) {
          data[accountId] = {
            withholdingRate,
            isWithholding
          };
          data[accountId].accountInfo = {
            ...account,
            accountName,
            accountType,
            accountTypeId,
            accountId,
            accountWChecksum,
            brokerageAssetType,
            isIncome2020: true,
            distributionTypeId,
            owners,
            retirementAccount,
            sourceAccountDescriptor,
            ticker,
            closedAccount
          };
        }

        const columnInfo = {
          distributionTypeId,
          pendingUpdateId
        };

        if (distributionTypeId === DISTRIBUTE_TO_FUND_ID) {
          const fundAccount = accountsById[targetAccountId] || {};
          if (fundAccount) {
            const {
              accountName,
              accountType,
              accountId,
              accountTypeId,
              ticker
            } = fundAccount;
            const accountWChecksum = formatAccountNumber(fundAccount);
            const owners = extractAndFormatOwners(fundAccount);
            const fundInfo = {
              accountName,
              accountId,
              accountType,
              accountTypeId,
              accountWChecksum,
              owners,
              ticker,
              //Adding this as a safeguard - account descriptor was only populated in the payload if the fund changed
              accountDescriptor: getAccountDescriptor(fundAccount),
              sourceAccountDescriptor
            };
            columnInfo.fundInfo = fundInfo;
          }
        } else if (distributionTypeId === DISTRIBUTE_TO_BANK_ID) {
          const {
            bankAgreementKey: { bankId }
          } = dividendCapitalGain;
          const bankAccount = bankAccounts.find(
            (bankAccount) =>
              bankAccount.bankAgreementKey &&
              bankAccount.bankAgreementKey.bankId === bankId
          );
          const dupeBankDetails = bankAccounts.find((bankAccount) =>
            areBanksWithoutIdEqual(
              bankAccount.bankAgreementKey,
              dividendCapitalGain.bankAgreementKey
            )
          );

          // seems unnecessary but because of bad bank ids we need pull bank details from duplicate match and retain submitted bank id
          if (!bankAccount && bankId && dupeBankDetails) {
            const bankDetails = {
              ...dupeBankDetails
            };

            bankDetails.bankId = bankId;
            columnInfo.bankInfo = bankDetails;
          } else {
            columnInfo.bankInfo = bankAccount;
          }
        }

        if (typeId === DIVIDEND_TYPE_ID) {
          data[accountId].dividends = columnInfo;
        } else {
          data[accountId].capitalGains = columnInfo;
        }
      }
    });

    return data;
  }
);

// Gets Brokerage Accounts for Dividends and Capital Gains Table
export const getDividendsAndCapitalGainsBrokerageTableDataV2 = createSelector(
  getIntradayAccounts,
  (intradayAccounts) => {
    const data = {};
    // iterate through intraday response that holds brokerage accounts
    intradayAccounts
      .filter((account) => !account.isViewOnly)
      .forEach((account) => {
        const {
          accountId,
          accountType,
          accountTypeId,
          retirementAccount,
          accountNumber,
          positions
        } = account;
        // each brokerage account has positions
        positions
          .filter((position) => {
            if (!position.brokerageDivCapGains) {
              return false;
            }
            const {
              brokerageDivCapGains: { brokerageAssetType = undefined },
              recordStatus = ''
            } = position;
            if (!brokerageAssetType && recordStatus === 'UP_TO_DATE')
              return false;
            return true;
          })
          .forEach((position) => {
            const {
              brokerageDivCapGains = {},
              cusip,
              name,
              ticker,
              closedAccount,
              recordStatus
            } = position;
            const {
              brokerageAssetType = undefined,
              distributionOptions = undefined
            } = brokerageDivCapGains;
            const accountName = name;
            const accountWChecksum = formatAccountNumber(account);
            const owners = extractAndFormatOwners(account);
            const key = `${accountId}-${cusip}`;
            if (brokerageAssetType !== null) {
              data[key] = {};
              data[key].accountInfo = {
                ...account,
                accountName,
                accountType,
                accountTypeId,
                accountId,
                accountWChecksum,
                brokerageAssetType,
                owners,
                retirementAccount,
                ticker,
                closedAccount,
                cusipId: cusip,
                sourceAccountDescriptor: {
                  ...distributionOptions[0]?.sourceAccountDescriptor,
                  accountName,
                  accountNumber,
                  accountType,
                  accountTypeId,
                  accountId,
                  accountWChecksum,
                  cusipId: cusip
                },
                recordStatus
              };

              // iterate through for dividends and capital gains distribution options
              // brokerage distribution types: reinvest or cash
              distributionOptions.forEach((distributionOption) => {
                const { distributionTypeId, pendingUpdateId, typeId } =
                  distributionOption;

                const columnInfo = {
                  distributionTypeId,
                  pendingUpdateId
                };

                if (typeId === DIVIDEND_TYPE_ID) {
                  data[key].dividends = columnInfo;
                } else {
                  data[key].capitalGains = columnInfo;
                }
              });
            }
          });
      });
    return data;
  }
);

export const getCombinedDividendsAndCapitalGainsData = createSelector(
  getDividendsAndCapitalGainsTableDataV2,
  getDividendsAndCapitalGainsBrokerageTableDataV2,
  (dividendsAndCapitalGainsData, dividendsAndCapitalGainsBrokerageData) => {
    const newCombinedTableData = {
      ...dividendsAndCapitalGainsData,
      ...dividendsAndCapitalGainsBrokerageData
    };
    return generateTableGroupRows(newCombinedTableData);
  }
);

export const getDividendsAndCapitalGainsRetirementTableData = createSelector(
  getDividendsCapitalGains,
  getAccountsById,
  (dividendsCapitalGains, accountsById) => {
    const { bankAccounts, distributionOptions, withholdingOptions } =
      dividendsCapitalGains;
    const data = {};

    distributionOptions.forEach((dividendCapitalGain) => {
      const {
        distributionTypeId,
        pendingUpdateId,
        sourceAccountDescriptor,
        typeId
      } = dividendCapitalGain;
      const sourceAccountId = sourceAccountDescriptor.accountId;
      const account = accountsById[sourceAccountId];
      if (isRetirementFund(account)) {
        const withholdingOption = withholdingOptions.find(
          (withholdingOption) =>
            withholdingOption.accountDescriptor.accountId === sourceAccountId
        );
        let withholdingRate = 0;
        let isWithholding = false;
        if (withholdingOption) {
          withholdingRate = withholdingOption.withholdingRate;
          isWithholding = !withholdingOption.withholdingExempt;
        }
        const {
          accountName,
          accountType,
          accountId,
          accountTypeId,
          retirementAccount,
          ticker,
          closedAccount
        } = account;
        const accountWChecksum = formatAccountNumber(account);
        const owners = extractAndFormatOwners(account);
        if (isNil(data[accountId])) {
          data[accountId] = {
            withholdingRate,
            isWithholding
          };
          data[accountId].accountInfo = {
            accountName,
            accountType,
            accountTypeId,
            accountId,
            accountWChecksum,
            isIncome2020: true,
            owners,
            retirementAccount,
            sourceAccountDescriptor,
            ticker,
            closedAccount
          };
        }

        const columnInfo = {
          distributionTypeId,
          pendingUpdateId
        };

        if (distributionTypeId === DISTRIBUTE_TO_FUND_ID) {
          const fundAccount = accountsById[sourceAccountId] || {};
          if (fundAccount) {
            const {
              accountName,
              accountType,
              accountId,
              accountTypeId,
              ticker
            } = fundAccount;
            const accountWChecksum = formatAccountNumber(fundAccount);
            const owners = extractAndFormatOwners(fundAccount);
            const fundInfo = {
              accountName,
              accountId,
              accountType,
              accountTypeId,
              accountWChecksum,
              owners,
              ticker,
              //Adding this as a safeguard - account descriptor was only populated in the payload if the fund changed
              accountDescriptor: getAccountDescriptor(fundAccount)
            };
            columnInfo.fundInfo = fundInfo;
          }
        } else if (distributionTypeId === DISTRIBUTE_TO_BANK_ID) {
          const {
            bankAgreementKey: { bankId }
          } = dividendCapitalGain;
          const bankAccount = bankAccounts.find(
            (bankAccount) =>
              bankAccount.bankAgreementKey &&
              bankAccount.bankAgreementKey.bankId === bankId
          );
          const dupeBankDetails = bankAccounts.find((bankAccount) =>
            areBanksWithoutIdEqual(
              bankAccount.bankAgreementKey,
              dividendCapitalGain.bankAgreementKey
            )
          );

          // seems unnecessary but because of bad bank ids we need pull bank details from duplicate match and retain submitted bank id
          if (!bankAccount && bankId && dupeBankDetails) {
            const bankDetails = {
              ...dupeBankDetails
            };
            bankDetails.bankId = bankId;
            columnInfo.bankInfo = bankDetails;
          } else {
            columnInfo.bankInfo = bankAccount;
          }
        }

        if (typeId === DIVIDEND_TYPE_ID) {
          data[accountId].dividends = columnInfo;
        } else {
          data[accountId].capitalGains = columnInfo;
        }
      }
    });

    return generateTableGroupRows(data);
  }
);

export const getBankAccountsById = createSelector(
  getDividendsCapitalGains,
  (dividendsCapitalGains) => {
    const { bankAccounts } = dividendsCapitalGains;
    const bankAccountsById = {};
    bankAccounts.forEach((bankAccount) => {
      const { bankAssociations } = bankAccount;
      if (bankAssociations) {
        const associatedIds = bankAssociations.map((association) => {
          const {
            accountDescriptor: { accountId }
          } = association;
          if (accountId) {
            return accountId;
          }
        });
        const uniqAccountIds = uniq(associatedIds);
        const filteredPendingAssociations = uniqAccountIds.filter((accountId) =>
          find(bankAssociations, (a) => {
            const {
              accountDescriptor: { accountId: associationAcctId }
            } = a;
            return associationAcctId === accountId && !a.pendingUpdate;
          })
        );
        filteredPendingAssociations.forEach((accountId) => {
          const bankAccountsForId = bankAccountsById[accountId] || [];
          bankAccountsForId.push(bankAccount);
          bankAccountsById[accountId] = bankAccountsForId;
        });
      }
    });
    return bankAccountsById;
  }
);

const getAvailableFunds = (state) => state.availableFunds;
const getDistributionToEdit = (state) => state.distributionToEdit;

export const getAvailableFundsForSelect = createSelector(
  getAvailableFunds,
  getDistributionToEdit,
  getAccountsById,
  (availableFunds, distributionToEdit, accountsById) => {
    const enhancedAvailableFunds = [];

    // Filter out 529 agreements
    // Filter out Brokerage - product 20
    // Filter out APP - product 110
    // if isIRA -> can only send to same agreement type
    // if !isIRA -> can send to any IRA and any other Taxable
    const {
      accountInfo: { accountTypeId: fromAccountTypeId }
    } = distributionToEdit;

    const filteredAvailableFundsByProduct = availableFunds.filter((account) => {
      const { productType } = account;
      if (productType) {
        return (
          !isBrokerageOrActivePlus(productType) &&
          !isCollegeSavings(productType)
        );
      }
    });

    const filterByEligibility = filteredAvailableFundsByProduct.filter(
      (eligibleAccount) => {
        const { accountId } = eligibleAccount;
        const account = accountsById[accountId];
        if (account && fromAccountTypeId) {
          if (isIRA(fromAccountTypeId)) {
            const { accountTypeId, owner } = account;
            return accountTypeId === fromAccountTypeId && owner;
          }
          return true;
        }
      }
    );
    filterByEligibility.forEach((fund) => {
      const { accountId } = fund;
      const account = accountsById[accountId];
      if (account) {
        const { accountName, accountType, accountTypeId, ticker } = account;
        const accountWChecksum = formatAccountNumber(account);
        const owners = extractAndFormatOwners(account);
        enhancedAvailableFunds.push({
          accountName,
          accountType,
          accountTypeId,
          accountId,
          accountWChecksum,
          owners,
          ticker,
          accountDescriptor: fund
        });
      }
    });

    return enhancedAvailableFunds.sort((a, b) =>
      a.accountName.localeCompare(b.accountName)
    );
  }
);

const breadcrumbLabelMap = {
  'bank-account-link': 'Bank Accounts',
  'contact-information': 'Contact Information',
  'trusted-contacts': 'Trusted Contacts',
  beneficiaries: 'Beneficiary on File',
  checkwriting: 'Checkwriting',
  profile: 'Profile',
  'cost-basis': 'Cost Basis',
  'customize-account-view': 'Customize Account View',
  'dividends-and-capital-gains': 'Dividends & Capital Gains Distributions',
  subscriptions: 'Subscriptions',
  'document-delivery': 'Document Delivery',
  'turbotax-benefits': 'TurboTax Discounts',
  'enhanced-personal-services-benefits': 'Enhanced Personal Services Benefits',
  'personal-services-benefits': 'Personal Services Benefits',
  'client-services-benefits': 'Client Services Benefits',
  'message-center': 'Message Center',
  'grant-view-access': 'Grant View Access',
  'alerts-and-notifications': 'Alerts & Notifications',
  'linked-bank-accounts': 'Bank Accounts'
};

export const getPreferencesBreadcrumbs = createSelector(getRouter, (router) => {
  const { location } = router;
  const { pathname, search = '' } = location;
  const pathnames = pathname.replace('/', '').split('/');
  const links = [
    {
      label: 'My Accounts',
      url: '/dashboard'
    }
  ];
  let base = '';
  pathnames.forEach((path) => {
    base = `${base}/${path}${search}`;
    links.push({
      label: breadcrumbLabelMap[path],
      url: base
    });
  });

  return links;
});

export const getMutualFunds = (state) => state.mutualFunds;

export const getUserMutualFundsWithIVData = createSelector(
  getMutualFunds,
  getUserMutualFunds,
  (mutualFunds, userMutualFunds) => {
    return userMutualFunds.map((fund) => {
      const ivFund = mutualFunds.find((f) => f.trpCode === fund.trpCode);
      return {
        ...ivFund,
        ...fund
      };
    });
  }
);

export const groupMutualFunds = createSelector(
  getMutualFunds,
  (mutualFunds) => {
    const groups = {};

    mutualFunds.forEach((mutualFund) => {
      const { broadAssetClassId } = mutualFund;
      const broadAssetClassName = getBroadAssetClassName(broadAssetClassId);
      const listOfAssets = groups[broadAssetClassName] || [];
      listOfAssets.push(mutualFund);
      groups[broadAssetClassName] = listOfAssets;
    });

    const listOfGroups = Object.entries(groups)
      .map(([broadAssetClassName, listOfAssets]) => ({
        broadAssetClassName,
        listOfAssets: listOfAssets.sort((a, b) => a.name.localeCompare(b.name))
      }))
      .sort((a, b) =>
        a.broadAssetClassName.localeCompare(b.broadAssetClassName)
      );

    return listOfGroups;
  }
);

export const getListOfMutualFundGroups = createSelector(
  getMutualFunds,
  (mutualFunds) => {
    const listOfMutualFundGroups = new Set();
    mutualFunds.forEach((mutualFund) =>
      listOfMutualFundGroups.add(mutualFund.broadAssetClassId)
    );
    return [...listOfMutualFundGroups];
  }
);

const getSelectedGroupId = (state) => state.customerPreferences;
export const getDefaultGroupByID = createSelector(
  getSelectedGroupId,
  (customerPreferences) => {
    return customerPreferences.defaultGroupBy;
  }
);

const getAccountFilteringSelections = (state) =>
  state.accountFilteringSelections;
export const getDefaultByGroupOptions = createSelector(
  getAccountFilteringSelections,
  (accountFilteringSelections) => {
    return accountFilteringSelections.grouping.options;
  }
);

const getCustomizeFilters = (state) => state.customizeAccountsFilters;
const OWNERSHIP = 'ownership';

export const getOrderedCustomizeAccounts = createSelector(
  getNonPurgedAccounts,
  getCustomizeFilters,
  (accounts, filtering) =>
    orderBy(
      accounts,
      groupingFunctionalMapping(filtering.grouping.selection, true)
    )
);

export const getCustomizeAccountsGroups = createSelector(
  getOrderedCustomizeAccounts,
  getCustomizeFilters,
  (accounts, filtering) => {
    const selection = filtering.grouping.selection;
    let accountGroups = groupBy(accounts, groupingFunctionalMapping(selection));
    accountGroups = map(accountGroups, (accountGroup) => {
      return groupBy(accountGroup, (account) => [
        accountGrouping(account),
        ownerGrouping(account)
      ]);
    });

    return accountGroups;
  }
);

export const getCustomizeAccountsViewTableData = createSelector(
  getCustomizeAccountsGroups,
  getCustomizeFilters,
  getBalancesCurrentDate,
  getView,
  (accountGroups, filteringSelections, currentDate, view) =>
    map(accountGroups, (accountGroup) => {
      if (!Array.isArray(accountGroup)) {
        const { selection } = filteringSelections.grouping;
        const sortedAccounts = sortBy(
          Object.values(accountGroup),
          (accounts) => accounts[0].accountType
        );
        const aggregateGroup = sortedAccounts.reduce((acc, accounts) => {
          let key = OWNERSHIP;
          const children = mapAccountsToTableData({
            filteringSelections,
            currentDate,
            view,
            transformAccount: (v) => v,
            isProductOrOwnership: true
          })(accounts);

          if (isProductSelection(selection)) {
            const { productType } = accounts[0];
            key = productTypeNameFromNumber(productType);
          }

          if (acc[key]) {
            acc[key].children = acc[key].children.concat(children);
            acc[key].accounts = acc[key].accounts.concat(accounts);
            return acc;
          }

          acc[key] = {
            children: [].concat(children),
            accounts: [].concat(accounts)
          };
          return acc;
        }, {});

        // Create total group header object, but replace children with the children created created previously
        const { accounts, children } = Object.values(aggregateGroup)[0];
        const positionsTableData = mapAccountsToTableData({
          filteringSelections,
          currentDate,
          view,
          transformAccount: (v) => v,
          isProductOrOwnership: false
        })(accounts);
        positionsTableData.children = children;
        return positionsTableData;
      }
      // Standard construction of the positionsTableData because accountGroup is an array of object.
      return mapAccountsToTableData({
        filteringSelections,
        currentDate,
        view,
        transformAccount: (v) => v
      })(accountGroup);
    })
);

const checkAccountGroup = (accounts, accountList) => {
  return (
    accounts.length ===
    accounts.reduce((count, x) => {
      return accountList.includes(x.details.name.accountId) ? count + 1 : count;
    }, 0)
  );
};

export const mapCustomAccountGroupData = (state) => {
  const { customAccountGroups } = state.customerPreferences;
  const accountGroups = getCustomizeAccountsViewTableData(state);
  const activeAccountGroup = getActiveCustomAccountGroup(customAccountGroups);
  if (!activeAccountGroup) {
    return accountGroups;
  }
  const accountIds = activeAccountGroup.accountDescriptors.map(
    (x) => x.accountId
  );
  return accountGroups.map((accGroup) => {
    accGroup.children.map((group) => {
      group.details.selected = checkAccountGroup(group.children, accountIds);
      group.children.map((account) => {
        account.details.selected = accountIds.includes(
          account.details.name.accountId
        );
        return account;
      });
      return group;
    });
    return accGroup;
  });
};

export const getAreAllAccountsHidden = createSelector(
  getNonPurgedAccounts,
  (accounts) => {
    return accounts.filter((account) => !account.hiddenAccount).length === 0;
  }
);

export const getAllAccountDescriptors = createSelector(
  getNonPurgedAccounts,
  (accounts) =>
    accounts.map((a) => {
      return {
        accountId: a.accountId,
        accountNumber: a.accountNumber,
        accountChecksum: a.accountChecksum,
        productType: a.productType,
        trpCode: a.trpCode,
        accountTypeId: a.accountTypeId,
        suppressFromView: a.hiddenAccount
      };
    })
);
